import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { SplitSupplyPartnerNoticeChannel } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'

import {
  type CivilDate,
  civilDateToInputValue,
  parseDateInputValue,
} from '~/models/civilDate'

/**
 * 他社の通告値 (登録 1 件) の識別と、 詳細画面の URL との相互変換。
 *
 * 自社の通告値の通は保存されず id を持たないが、 他社の通告値の登録は 1 件 (断面 × 対象日 × エリア × 他社) で id を持つ。
 * 詳細画面は自社の通告値と同じ route で、 `receipt=<登録の id>` の query で入る (月間は表示する日 `day` も持つ)。
 */

const PARAM_NAMES = {
  id: 'receipt',
  deliveryDate: 'day',
} as const

/**
 * 「追加」 で新しく作った登録の詳細を、 最初から編集モードで開くための route state。
 * query ではなく state にするのは、 URL に残して共有・ブックマークされたり、 戻る・再読込で勝手に
 * 編集モードになったりしないため (詳細は受け取ったらすぐ state を消す)。
 */
export const START_EDITING_STATE = { startEditing: true } as const

export function isStartEditingState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'startEditing' in state &&
    state.startEditing === true
  )
}

/** 詳細画面への URL の query (`?` を含まない)。 */
export function partnerNoticeDetailSearch(
  id: string,
  deliveryDate: CivilDate,
): string {
  const params = new URLSearchParams()
  params.set(PARAM_NAMES.id, id)
  params.set(PARAM_NAMES.deliveryDate, civilDateToInputValue(deliveryDate))
  return params.toString()
}

export type PartnerNoticeLocation = {
  id: string
  deliveryDate: CivilDate
}

/**
 * 詳細画面の query から登録の id と表示する日を読む。 欠けた・壊れた query は undefined
 * (呼び出し側は「登録を特定できない」を案内する)。
 */
export function parsePartnerNoticeDetailSearch(
  search: URLSearchParams,
): PartnerNoticeLocation | undefined {
  const id = search.get(PARAM_NAMES.id)
  const rawDay = search.get(PARAM_NAMES.deliveryDate)
  if (
    id === null ||
    id === '' ||
    rawDay === null ||
    !/^\d{4}-\d{2}-\d{2}$/.test(rawDay)
  ) {
    return undefined
  }
  const deliveryDate = parseDateInputValue(rawDay)
  return deliveryDate === undefined ? undefined : { id, deliveryDate }
}

/** 登録方法の表示文言の key (Record で全値を網羅する)。 未指定は文言を持たない。 */
export const PARTNER_NOTICE_CHANNEL_KEYS: Record<
  SplitSupplyPartnerNoticeChannel,
  string | undefined
> = {
  [SplitSupplyPartnerNoticeChannel.UNSPECIFIED]: undefined,
  [SplitSupplyPartnerNoticeChannel.XML]:
    'page:splitSupplyPlan.partner.channel.xml',
  [SplitSupplyPartnerNoticeChannel.CSV]:
    'page:splitSupplyPlan.partner.channel.csv',
  [SplitSupplyPartnerNoticeChannel.MANUAL]:
    'page:splitSupplyPlan.partner.channel.manual',
}

/**
 * 登録のあとで手直しされたか (最終更新が登録日時と違うか)。 追加した直後は最終更新 = 登録日時なので、
 * 手直しされるまでは最終更新を出さない。
 */
export function isPartnerNoticeEdited(
  createdAt: Timestamp | undefined,
  updatedAt: Timestamp | undefined,
): boolean {
  if (createdAt === undefined || updatedAt === undefined) return false
  return (
    createdAt.seconds !== updatedAt.seconds ||
    createdAt.nanos !== updatedAt.nanos
  )
}
